import React from "react";
import styles from "./LeaveWarningModal.module.css";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Optional override for the body copy. Defaults to the standard
   * "you'll lose your design" warning.
   */
  message?: string;
};

/**
 * Confirmation overlay shown when a guest is about to leave the
 * builder without registering — clicking the header Back button is
 * the primary trigger. Visual + button conventions match the
 * SignupModal's Brand Ambassador warning (white-on-red primary,
 * outlined neutral secondary) so users who've already seen that flow
 * recognize the pattern.
 *
 * Kept as a standalone component (not embedded in SignupModal) so
 * the back-button entry point doesn't pull in the whole signup form's
 * deps. The signup form's BA-warning still lives inline there for the
 * same reason — keeping that overlay's lifecycle tied to the SignupModal
 * the user is already inside.
 */
export const LeaveWarningModal: React.FC<Props> = ({ isOpen, onCancel, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        // Backdrop click dismisses the warning. `stopPropagation` so
        // we don't bubble up to any ancestor click handlers that
        // could re-open it or interpret the click as a canvas action.
        e.stopPropagation();
        onCancel();
      }}
    >
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        <div className={styles.title}>Leave Creative Studio?</div>
        <div className={styles.body}>
          {message ||
            "If you leave without creating an account, the design you've started will be lost. Are you sure you want to continue?"}
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={onCancel}
            data-cypress-id="guest-leave-warning-cancel"
          >
            Stay here
          </button>
          <button
            type="button"
            className={styles.confirm}
            onClick={onConfirm}
            data-cypress-id="guest-leave-warning-confirm"
          >
            Yes, leave
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveWarningModal;
